import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

export type CapacityHeat = 'open' | 'filling' | 'near' | 'full';

export interface CapacityOverviewCell {
  startsAt: string;
  outOfHours: boolean;
  bookedSeats: number;
  blockedSeats: number;
  seatsLeft: number;
  heat: CapacityHeat;
  bookings?: CapacityOverviewBooking[];
}

export interface CapacityOverviewBooking {
  bookingId: string;
  reference: string;
  guestName: string;
  seats: number;
}

export interface CapacityOverviewRoom {
  roomId: string;
  name: string;
  capacity: number;
}

export interface CapacityOverviewBlock {
  id: string;
  roomId: string;
  startsAt: string;
  durationMinutes: number;
  blockedSeats: number;
  reason: string;
}

export interface CapacityOverview {
  venueId: string;
  day: string;
  timezone: string;
  capacityTotal: number;
  closed: boolean;
  rooms: CapacityOverviewRoom[];
  cells: CapacityOverviewCell[];
  blocks: CapacityOverviewBlock[];
}

export interface CreateCapacityBlockInput {
  roomId: string;
  startsAt: string;
  durationMinutes: number;
  blockedSeats: number;
  reason: string;
}

const capacityOverviewKey = (venueId: string, day: string) =>
  ['venues', venueId, 'capacity-overview', day] as const;

const capacityOverviewPrefix = (venueId: string) =>
  ['venues', venueId, 'capacity-overview'] as const;

export const getCapacityOverview = (venueId: string, day: string): Promise<CapacityOverview> =>
  unwrap(
    apiClient.GET('/api/v1/venue-workspace/venues/{venueId}/capacity-overview', {
      params: { path: { venueId }, query: { day } },
    } as never),
  ) as Promise<CapacityOverview>;

export const createCapacityBlock = (
  venueId: string,
  input: CreateCapacityBlockInput,
): Promise<CapacityOverviewBlock> =>
  unwrap(
    apiClient.POST('/api/v1/venue-workspace/venues/{venueId}/capacity-blocks', {
      params: { path: { venueId } },
      body: input,
    } as never),
  ) as Promise<CapacityOverviewBlock>;

export function useCapacityOverview(venueId: string, day: string, enabled = true) {
  return useQuery({
    queryKey: capacityOverviewKey(venueId, day),
    queryFn: () => getCapacityOverview(venueId, day),
    enabled: enabled && !!venueId && !!day,
    refetchInterval: 10000,
    refetchOnWindowFocus: true,
  });
}

export function useCreateCapacityBlock(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCapacityBlockInput) => createCapacityBlock(venueId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: capacityOverviewPrefix(venueId) });
    },
  });
}
